import { startRun, getRun } from '../api_scripts/runsClient';
import { updateBatchProgress } from '../api_scripts/batchesClient';

const TERMINAL = new Set(['COMPLETED', 'FAILED', 'ERROR']);
// Deadline covers both the call phase AND the post-call reporter (Transcribe
// + evaluator + render). When post-call is treated as background work
// (waitUntilCallEnded = true), the dispatcher moves on at ~90s and the full
// deadline is effectively unused; this value only matters when a run is being
// polled synchronously to its real terminal state.
const DEFAULT_DEADLINE_MS = 15 * 60_000;
const DEFAULT_POLL_INTERVAL_MS = 5000;

// Returned by waitForTerminal when a batch dispatcher is configured to move
// on as soon as the call ends (hungUpAt landed in the TestRun row). The real
// COMPLETED/FAILED verdict is still being computed by the reporter Lambda in
// the background — the Batches tab surfaces this as "evaluating" and refetches
// per-run final status later. See runBatchSequentially({ waitUntilCallEnded }).
export const CALL_ENDED_PENDING_EVAL = 'CALL_ENDED_PENDING_EVAL';

/**
 * Canonical signature of a plan's preconditions — same set of label=value
 * pairs (order-insensitive) → same signature. Empty when the plan carries no
 * preconditions. Used both to reorder a queue (group adjacent) and to decide
 * whether the runtime gate needs to re-pause between plans.
 */
export function preconditionSignature(plan) {
  const list = plan?.metadata?.preconditions;
  if (!Array.isArray(list) || list.length === 0) return '';
  return list
    .filter((p) => p && p.label && p.value)
    .map((p) => `${String(p.label).trim()}=${String(p.value).trim()}`)
    .sort()
    .join('|');
}

/**
 * Reorder a batch so plans sharing the same precondition signature run
 * back-to-back — the engineer flips the IVR switches once per group instead
 * of once per plan. Plans with no preconditions float to the front (run them
 * with no setup), then each precondition group runs as a contiguous block.
 * Stable within each group; first-appearance order across groups so the
 * engineer's ordering choices upstream still matter.
 */
export function reorderForPreconditionGrouping(plans) {
  if (!Array.isArray(plans) || plans.length < 2) return plans;
  const groups = new Map();
  const groupOrder = [];
  for (const plan of plans) {
    const sig = preconditionSignature(plan);
    if (!groups.has(sig)) {
      groups.set(sig, []);
      groupOrder.push(sig);
    }
    groups.get(sig).push(plan);
  }
  // No-precondition group ('') first so easy plans run without waiting.
  groupOrder.sort((a, b) => {
    if (a === '' && b !== '') return -1;
    if (b === '' && a !== '') return 1;
    return 0;
  });
  const out = [];
  for (const sig of groupOrder) out.push(...groups.get(sig));
  return out;
}

/**
 * Poll /runs/{id} until the run reaches a terminal state. Returns the final
 * status string ('COMPLETED' | 'FAILED' | 'ERROR' | 'TIMEOUT' | 'CANCELLED'
 * | 'CALL_ENDED_PENDING_EVAL' | last-known).
 *
 * `onTick(status)` fires once per status transition so callers can update UI.
 *
 * `waitUntilCallEnded = true` (opt-in): return as soon as the TestRun row
 * carries `timeline.hungUpAt` (i.e., the Chime SDK emitted a HANGUP event
 * for this call). The reporter Lambda is still running in the background
 * doing Transcribe + evaluator + render — the real COMPLETED/FAILED verdict
 * lands on the row several minutes later. Callers get CALL_ENDED_PENDING_EVAL
 * to signal "the call portion is done; the batch can move on."
 *
 * The point of the opt-in: batches of real phone calls spend ~90s dialing
 * and executing steps, then 5-10 minutes in post-call processing. Blocking
 * the next call on the post-call work makes a 20-plan batch take 2-3 hours
 * when the real wall-clock work is 30 minutes. See
 * runBatchSequentially({ waitUntilCallEnded }).
 */
export async function waitForTerminal(
  testRunId,
  onTick,
  {
    deadlineMs = DEFAULT_DEADLINE_MS,
    pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
    waitUntilCallEnded = false
  } = {}
) {
  const deadline = Date.now() + deadlineMs;
  let last = null;
  while (Date.now() < deadline) {
    try {
      const row = await getRun(testRunId);
      if (row?.status && row.status !== last) {
        last = row.status;
        onTick?.(last);
      }
      if (row?.status && TERMINAL.has(row.status)) return row.status;
      // Fast-path exit for batch dispatchers: once the call is physically
      // ended (hungUpAt set by sma-handler), the next plan can dial. The
      // reporter will catch up asynchronously and update row.status later.
      if (waitUntilCallEnded && row?.timeline?.hungUpAt) {
        onTick?.(CALL_ENDED_PENDING_EVAL);
        return CALL_ENDED_PENDING_EVAL;
      }
    } catch {
      // transient — keep polling
    }
    await new Promise((r) => setTimeout(r, pollIntervalMs));
  }
  return last ?? 'TIMEOUT';
}

/**
 * Run a batch of plans sequentially, one call at a time. Calls do NOT overlap:
 * the next plan only dials after the previous reaches a terminal state. This
 * is critical when all plans target the same destination DID — overlapping
 * calls collide on the destination phone and produce empty recordings.
 *
 * `plans`: array of TestPlan objects (already runnable — target.destination
 *   and target.fromDid filled in OR provided via `overrides`).
 * `overrides`: { destination?, fromDid? } — wins over each plan's target.
 * `diagrams`: optional list of persisted-diagram refs to attach to every run
 *   (used by the reporter to embed diagrams in the PDF).
 * `onProgress(progress)`: fires after every state change. Progress shape:
 *   { total, done, current: { name, status } | null, results: [...], errors: [...] }
 * `onPreconditionChange(plan, { index, total })`: optional async hook fired
 *   right before dialing a plan IF that plan's precondition signature differs
 *   from the previously-dialed plan's signature. The hook resolves with one
 *   of `{ proceed: true }` (dial now), `{ skip: true }` (record skipped,
 *   continue), `{ cancel: true }` (stop the batch). The runner waits on this
 *   promise, so callers can render a modal and only resolve when the
 *   engineer clicks. Plans whose signature matches the previous plan don't
 *   fire the hook — flipping the same flag twice is wasted ceremony.
 *
 * Returns the final progress object once every plan reaches a terminal state.
 */
export async function runBatchSequentially({
  plans,
  overrides = {},
  diagrams = [],
  onProgress,
  onPreconditionChange,
  /** Optional in-browser batch row id. When set, the runner POSTs to
   *  /batches/{id}/progress after each plan terminates so the Batches tab
   *  in the UI reflects in-browser batches the same way it shows
   *  server-driven ones. Failures on the progress POST are logged + ignored
   *  so a transient network blip doesn't stop the batch. */
  batchId = null,
  /** When true, the dispatcher moves on to the next plan the moment the
   *  current call hangs up (hungUpAt lands in the TestRun row) instead of
   *  blocking on the full reporter cycle (recording wait + Transcribe +
   *  evaluator, 5-10 min). Call result is posted to the batch as
   *  CALL_ENDED_PENDING_EVAL; the real verdict is reconciled later.
   *  Default true — the only reason to set false is to force synchronous
   *  waits for debugging a specific run. */
  waitUntilCallEnded = true,
  planName = (plan, i) => plan?.metadata?.name || plan?.metadata?.id || `Plan ${i + 1}`
}) {
  let progress = {
    total: plans.length,
    done: 0,
    current: null,
    results: [],
    errors: []
  };
  const emit = (next) => {
    progress = next;
    onProgress?.(progress);
  };
  emit(progress);

  let lastDialedSig = null;
  let cancelled = false;
  for (let i = 0; i < plans.length; i++) {
    const plan = plans[i];
    const name = planName(plan, i);
    const sig = preconditionSignature(plan);
    if (onPreconditionChange && sig && sig !== lastDialedSig) {
      emit({ ...progress, current: { name, status: 'awaiting-setup' } });
      let decision = { proceed: true };
      try {
        decision = (await onPreconditionChange(plan, { index: i, total: plans.length })) || { proceed: true };
      } catch {
        decision = { cancel: true };
      }
      if (decision.cancel) {
        cancelled = true;
        emit({ ...progress, current: null });
        break;
      }
      if (decision.skip) {
        emit({
          ...progress,
          done: progress.done + 1,
          current: null,
          results: [
            ...progress.results,
            { index: i, name, testRunId: null, finalStatus: 'SKIPPED' }
          ]
        });
        if (batchId) {
          // Best-effort: a 4xx/5xx here doesn't stop the batch.
          updateBatchProgress(batchId, {
            planIndex: i,
            name,
            finalStatus: 'SKIPPED'
          }).catch(() => {});
        }
        continue;
      }
    }
    const cloned = {
      ...plan,
      target: {
        ...(plan.target || {}),
        ...(overrides.destination ? { destination: overrides.destination } : {}),
        ...(overrides.fromDid ? { fromDid: overrides.fromDid } : {})
      }
    };
    emit({ ...progress, current: { name, status: 'dialing' } });
    const startedAt = new Date().toISOString();
    try {
      const res = await startRun(cloned, {
        idempotencyKey: crypto.randomUUID(),
        ...(diagrams.length > 0 ? { diagrams } : {})
      });
      const finalStatus = await waitForTerminal(
        res.testRunId,
        (s) => {
          emit({ ...progress, current: { name, status: s } });
        },
        { waitUntilCallEnded }
      );
      emit({
        ...progress,
        done: progress.done + 1,
        current: null,
        results: [
          ...progress.results,
          { index: i, name, testRunId: res.testRunId, finalStatus }
        ]
      });
      if (batchId) {
        updateBatchProgress(batchId, {
          planIndex: i,
          name,
          testRunId: res.testRunId,
          finalStatus,
          startedAt,
          finishedAt: new Date().toISOString()
        }).catch(() => {});
      }
      lastDialedSig = sig;
    } catch (err) {
      const msg = err?.response?.data?.message || err.message;
      emit({
        ...progress,
        done: progress.done + 1,
        current: null,
        errors: [...progress.errors, { index: i, name, error: msg }]
      });
      if (batchId) {
        updateBatchProgress(batchId, {
          planIndex: i,
          name,
          finalStatus: 'ERROR',
          startedAt,
          finishedAt: new Date().toISOString(),
          error: msg
        }).catch(() => {});
      }
      // The engineer already flipped the switches for this signature before
      // the dial blew up — re-prompting them next iteration would be churn.
      // Track the signature as if the dial succeeded.
      lastDialedSig = sig;
    }
  }
  return { ...progress, cancelled };
}
